import React, { useState, useEffect, Fragment } from 'react';
import generalController from '../../../controllers/generalController';
import TrimSelectorsCarousel from './TrimSelectorsCarousel/TrimSelectorsCarousel';
import TrimSpecAccordions from './TrimSpecAccordions/TrimSpecAccordions';
import { Svg } from '../../1_Atoms/Svg/Svg';
import Link from '../../1_Atoms/Link/Link';
import { languageController } from '../../../controllers/language/languageController';
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';
import ReactHtmlParser from 'react-html-parser';

interface TrimType {
  name: string;
  url_slug: string;
}

interface Trim {
  trim_type: TrimType;
  visual: any;
}

interface Carline {
  title: string;
  url_slug: string;
}

interface Year {
  title: string;
  url_slug: string;
}

interface ModelYear {
  show_brochure?: boolean;
}

interface VehicleData {
  trim: Trim;
  model_code: string;
  option_code: string;
  carline: Carline;
  year: Year;
  model_year?: ModelYear;
  specifications?: SpecificationCategory;
}

interface SpecificationCategory {
  [categoryName: string]: SpecificationItem[];
}

interface SpecificationItem {
  Title: string;
  Data: string;
  type?: string;
}

interface ModelData {
  [trimSlug: string]: VehicleData;
}

interface TrimOption {
  slug: string;
  trim: string;
  name: string;
}

interface SpecCompareProps {
  formExtraParams: {
    backButtonLink?: string;
    downloadSpecsCTALink?: string;
    downloadBrochureCTALink?: string;
    models: string;
  };
}

const SpecCompare: React.FC<SpecCompareProps> = ({ formExtraParams }) => {
  const { backButtonLink, downloadSpecsCTALink, downloadBrochureCTALink, models } = formExtraParams;

  const [modelData, setModalData] = useState<ModelData | null>(null);
  const [trimsOrder, setTrimsOrder] = useState<string[]>([]);
  const [trimsOptions, setTrimsOptions] = useState<TrimOption[] | null>(null);
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isLoaded, setItLoaded] = useState<boolean>(false);

  const lang = generalController.detectLanguage();
  
  document.querySelector('html')!.style.overflow = 'hidden';

  const showDownloadSpecsButton = (document.querySelector('input[data-mz-form-field-name="showDownloadSpecsButton"]') as HTMLInputElement)?.value !== "false";
  const showDownloadBrochureButton = modelData && trimsOrder.length > 0 ? (modelData[trimsOrder[0]]?.model_year?.show_brochure ?? false) : false;
  const showDesignYoursButton = (document.querySelector('input[data-mz-form-field-name="showDesignYoursButton"]') as HTMLInputElement)?.value !== "false";
  
  console.log(showDownloadBrochureButton);
  console.log(showDownloadSpecsButton);
  console.log(showDesignYoursButton);

  const urlParams = new URLSearchParams(window.location.search);
  const firstTrim = urlParams.get('trim');
  
  useEffect(() => {
    const fetchModelData = async () => {
      try {
        // 'models' has a sequence of model codes concatenated with a pipe |.
        const modelStrings = models.split('|');
        if (firstTrim?.length === 11) {
          modelStrings.sort(function(x, y) {
            return x === firstTrim ? -1 : y === firstTrim ? 1 : 0;
          });
        }

        // Fetch vehicle data.
        const res = await handleRequest('post', getEndpointUrl('get-specs') || '', { cars: modelStrings });

        if (res.data.length > 0) {
          const vehicleData: VehicleData[] = res.data;
          // Convert vehicleData to object for easy access of each trim data.
          const vehicleDataFormatted = vehicleData.reduce<ModelData>((prev, current) => {
            const { trim } = current;
            return { ...prev, [trim.trim_type.url_slug]: current };
          }, {});
          // Set the whole model data.
          setModalData(vehicleDataFormatted);
          // Set the default trims order.
          const trimOrderArr = vehicleData.map(({ trim }) => trim.trim_type.url_slug);
          setTrimsOrder(trimOrderArr);
          
          // Set the trim options used in select fields.
          const trimOptionsArr: TrimOption[] = vehicleData.map(vehicle => ({
            slug: vehicle.trim.trim_type.url_slug,
            trim: `${vehicle.model_code}/${vehicle.option_code}`,
            name: vehicle.trim.trim_type.name
          }));
          setTrimsOptions(trimOptionsArr);
          // Set loading as done.
          setItLoaded(true);
        } else {
          console.error('Error: vehicleData does not exist in fetchModelData().');
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchModelData();
  }, [models, firstTrim]);

  const generateTitle = (modelData: ModelData | null, isOnlyTitle: boolean = false): string | JSX.Element | JSX.Element[] => {
    if (modelData && trimsOrder.length > 0) {
      const firstTrim = modelData[trimsOrder[0]];
      const { carline, year } = firstTrim;

      if (isOnlyTitle) {
        return ReactHtmlParser(carline.title);
      } else {
        if (lang === "fr")
          return ReactHtmlParser(carline.title) + ' ' + year.title;
        else
          return year.title + ' ' + ReactHtmlParser(carline.title);
      }
    }

    return '';
  };

  const genereatePDFURL = (isSpec: boolean, modelData: ModelData | null): string => {
    if (modelData && trimsOrder.length > 0) {
      const firstTrim = modelData[trimsOrder[0]];
      const { carline, year } = firstTrim;
      const pdfType = isSpec ? 'specs' : 'brochure';
      let URL = `/globalassets/mazda-canada/${lang}/pdf/brochures/vehicles/${carline.url_slug}/${year.title}-mazda-${carline.url_slug.toLowerCase()}-${pdfType}-${lang}.pdf`;
      return URL;
    }
    return '';
  };

  const specsPDF = downloadSpecsCTALink ? downloadSpecsCTALink : genereatePDFURL(true, modelData);
  const brochurePDF = downloadBrochureCTALink ? downloadBrochureCTALink : genereatePDFURL(false, modelData);

  return (
    <div className="mz-specs-compare">
      <div className="mz-table">
        <div className="mz-table-container mz-full-width">
          <div className="mz-specs-compare__back">
            <div className="mz-container">
              <Link
                href={backButtonLink || ''}
                text={`${languageController.getCopy('specCompare', 'backButton')} ${generateTitle(modelData, true)}`}
                className="mz-btn mz-btn--secondary mz-full-width"
                icon={<Svg type="chevron_small" size={0.75} rotation="180" />}
                iconPosition="left"
              />
            </div>
          </div>
      
          <div className="mz-specs-compare__top">
            <h2 className="mz-specs-compare__top-heading">{generateTitle(modelData)}</h2>
            <h4 className="mz-specs-compare__top-sub-heading">{languageController.getCopy('specCompare', 'subHeading')}</h4>
          </div>
          
          {
            isLoaded && modelData && trimsOptions && (
              <Fragment>
                <table role="table" className="mz-full-width" cellPadding={0} cellSpacing={0}>
                  <TrimSelectorsCarousel
                    modelData={modelData}
                    trimsOrder={trimsOrder}
                    trimsOptions={trimsOptions}
                    activeSlide={activeSlide}
                    setTrimsOrder={setTrimsOrder}
                    setActiveSlide={setActiveSlide}
                    showDesignYoursButton={showDesignYoursButton}

                  />

                  <TrimSpecAccordions
                    modelData={modelData}
                    trimsOrder={trimsOrder}
                    activeSlide={activeSlide}
                  />
                </table>
              </Fragment>
            )
          }
        
        </div>
      </div>
      
      {
        (showDownloadSpecsButton || showDownloadBrochureButton) && (
          <div className="mz-container-flex mobile">
            {showDownloadSpecsButton && (
            <div className="mz-specs-compare__download">
              <Link
                href={specsPDF}
                text={languageController.getCopy('specCompare', 'downloadSpecs') || ''}
                className="mz-btn mz-btn--secondary"
                icon={<Svg type="pdf_outline" size={1.5} />}
                iconPosition="left"
                download={true}
              />
            </div>)}
            { 
              /* Download brochure is now used a Design Yours button */
              isLoaded && showDownloadBrochureButton && ( 
                <div className="mz-specs-compare__download">
                  <Link
                    href={brochurePDF}
                    text={languageController.getCopy('specCompare', 'downloadBrochure') || ''}
                    className="mz-btn mz-btn--secondary"
                    icon={<Svg type="pdf_outline" size={1.5} />}
                    iconPosition="left"
                    download={true}
                  />
                </div>
              )
            }
          </div>
        )
      }
    </div>
  );
};

export default SpecCompare;
